import { List, Paper, Stack, Text, Title } from '@mantine/core'
import type { MapaDoFluxo, NumerosDoFluxo } from '@/api/chamados'
import { chamados, listaDeEstados } from './textosDoFluxo'
import type { Visao } from './visaoDoFluxo'

interface Props { mapa: MapaDoFluxo; numeros?: NumerosDoFluxo; visao: Visao }

const FAIXAS: { chave: keyof NumerosDoFluxo['sla']; rotulo: string }[] = [
  { chave: 'vencidos', rotulo: 'com prazo vencido' },
  { chave: 'em_risco', rotulo: 'em risco' },
  { chave: 'no_prazo', rotulo: 'no prazo' },
  { chave: 'pausados', rotulo: 'com o prazo pausado' },
  { chave: 'sem_prazo', rotulo: 'sem prazo (categoria sem SLA)' },
]

/** As regras do SLA, lidas do backend, e os números de agora (spec 2026-10-26 §3.1.5, §3.3). */
export function ComoOPrazoCorre({ mapa, numeros, visao }: Props) {
  const r = mapa.regras_sla
  const pausam = listaDeEstados(mapa.pausam, visao).join(', ')
  const primeira = listaDeEstados(r.primeira_resposta_conta_em, visao).join(' e ')
  const faixas = numeros ? FAIXAS.filter((f) => numeros.sla[f.chave] > 0) : []
  const cumprimento = numeros?.cumprimento
  return (
    <Paper withBorder p="md" radius="md">
      <Stack gap="md">
        <Title order={3} size="h4">Como o prazo corre</Title>
        <List spacing={6} size="sm">
          <List.Item>O prazo conta do início do ciclo atual, ou da abertura do chamado.</List.Item>
          <List.Item>Chamado de categoria sem SLA não tem prazo.</List.Item>
          <List.Item>
            Em horas úteis ({r.horario_util}) quando a categoria pede; senão, em horas corridas.
          </List.Item>
          <List.Item>Pausa em {pausam}, quando a categoria é das que pausam.</List.Item>
          <List.Item>O prazo da primeira resposta conta enquanto o chamado está em {primeira}.</List.Item>
          <List.Item>
            Fica em risco quando sobra {Math.round(r.limiar_de_risco * 100)}% do prazo ou menos.
          </List.Item>
          <List.Item>
            Reabrir recomeça o prazo e, se o ciclo anterior tinha vencido, a prioridade vira crítica.
          </List.Item>
          <List.Item>
            Entregue, o solicitante tem {r.prazo_validacao_horas} h para validar; sem validação, o
            chamado fecha sozinho.
          </List.Item>
          <List.Item>
            Resolvido fecha sozinho depois de {r.prazo_fechar_resolvido_dias} dias sem mudança, salvo
            prazo próprio do chamado ou da categoria.
          </List.Item>
          {visao === 'atendimento' && (
            <List.Item>Mudança (GMUD) não gera alerta nem escalonamento de prazo.</List.Item>
          )}
        </List>

        {numeros && numeros.abertos_total > 0 && (
          <Stack gap={4}>
            <Text fw={600}>
              {numeros.escopo === 'own'
                ? `Agora: seus ${chamados(numeros.abertos_total)} em aberto`
                : `Agora: ${chamados(numeros.abertos_total)} em aberto`}
            </Text>
            {faixas.map((f) => (
              <Text key={f.chave} size="sm" ff="monospace">
                {numeros.sla[f.chave]} {f.rotulo}
              </Text>
            ))}
          </Stack>
        )}

        {cumprimento && (
          <Text size="sm">
            {cumprimento.total_with_sla > 0
              ? <>Dos chamados abertos nos últimos 30 dias e já fechados, <Text span ff="monospace" fw={600}>
                  {cumprimento.within_sla} de {cumprimento.total_with_sla}</Text> fecharam no prazo.</>
              : 'Nenhum chamado com prazo aberto nos últimos 30 dias foi fechado ainda.'}
          </Text>
        )}
      </Stack>
    </Paper>
  )
}
